/**
 * Memo-page mock fixtures — Drug X Launch Performance Analysis.
 *
 * Served when storyId === "mock-memo-uuid-001".
 * Mirrors the exact shapes the backend returns so StoryMemoDocument
 * renders correctly in local dev without a running server.
 */
import type {
  CanvasDatasetApi,
  CanvasManifestApi,
  CanvasWidgetApi,
} from "./storyCanvas.types";

export const MOCK_MEMO_STORY_ID = "mock-memo-uuid-001";
export const MOCK_MEMO_WORKSPACE_ID = "mock-workspace-uuid-001";

const MANIFEST_LATENCY_MS = 200;
const WIDGET_LATENCY_MS: Record<string, number> = {
  "memo-kpi-1":       400,
  "memo-kpi-2":       600,
  "memo-kpi-3":       800,
  "memo-narrative-1": 1000,
  "memo-narrative-2": 1200,
  "memo-chart-1":     1500,
  "memo-chart-2":     1900,
  "memo-chart-3":     2300,
  "memo-insight-1":   2600,
};
const DATASET_LATENCY_MS = 300;

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ─── Manifest ──────────────────────────────────────────────────────────────────

export const MEMO_MANIFEST: CanvasManifestApi = {
  canvas_id: "mock-memo-canvas-uuid-001",
  status: "ready",
  items: [
    { item_id: "memo-kpi-1",       type: "kpi",       position: { x: 0, y: 0,  w: 4,  h: 4 } },
    { item_id: "memo-kpi-2",       type: "kpi",       position: { x: 4, y: 0,  w: 4,  h: 4 } },
    { item_id: "memo-kpi-3",       type: "kpi",       position: { x: 8, y: 0,  w: 4,  h: 4 } },
    { item_id: "memo-narrative-1", type: "narrative", position: { x: 0, y: 4,  w: 12, h: 6 } },
    { item_id: "memo-narrative-2", type: "narrative", position: { x: 0, y: 10, w: 12, h: 8 } },
    { item_id: "memo-chart-1",     type: "chart",     position: { x: 0, y: 18, w: 12, h: 20 } },
    { item_id: "memo-chart-2",     type: "chart",     position: { x: 0, y: 38, w: 12, h: 20 } },
    { item_id: "memo-chart-3",     type: "chart",     position: { x: 0, y: 58, w: 12, h: 20 } },
    { item_id: "memo-insight-1",   type: "insight",   position: { x: 0, y: 78, w: 12, h: 20 } },
  ],
};

// ─── Widget fixtures ───────────────────────────────────────────────────────────

export const MEMO_WIDGETS: Record<string, CanvasWidgetApi> = {
  "memo-kpi-1": {
    item_id: "memo-kpi-1",
    type: "kpi",
    name: "Sales target achievement",
    value: "107.7%",
    delta: "+7.7%",
    delta_pct: "+7.7%",
    trend: "up",
    comparison_label: "last six months",
    description: {
      title: ">109% achievement",
      body: "Drug X has consistently **exceeded sales targets over the past 6 months**, with particularly strong performance in June–July 2024 (>109% achievement). Recent months show stabilized growth at **~105–106% of target**.",
    },
  },
  "memo-kpi-2": {
    item_id: "memo-kpi-2",
    type: "kpi",
    name: "Market share growth rate",
    value: "3.2% (NBRx)",
    delta: "+1.4%",
    delta_pct: "+1.4%",
    trend: "up",
    comparison_label: "points over the past 12 weeks",
    description: {
      title: ">109% achievement",
      body: "Drug X has consistently **exceeded sales targets over the past 6 months**, with particularly strong performance in June–July 2024 (>109% achievement). Recent months show stabilized growth at **~105–106% of target**.",
    },
  },
  "memo-kpi-3": {
    item_id: "memo-kpi-3",
    type: "kpi",
    name: "Top growth region",
    value: "Texas",
    delta: "+18.5%",
    delta_pct: "+18.5%",
    trend: "up",
    comparison_label: "over the last month",
    description: {
      title: ">109% achievement",
      body: "Drug X has consistently **exceeded sales targets over the past 6 months**, with particularly strong performance in June–July 2024 (>109% achievement). Recent months show stabilized growth at **~105–106% of target**.",
    },
  },
  "memo-narrative-1": {
    item_id: "memo-narrative-1",
    type: "narrative",
    title: "Executive summary",
    body: "Drug X launch is performing **strongly**, exceeding sales targets across all regions with **consistent growth** over the **last 6 months**. Market share is steadily increasing, with peak performance driven by key regional gains and improved patient adherence.",
    accent: false,
  },
  "memo-narrative-2": {
    item_id: "memo-narrative-2",
    type: "narrative",
    title: "Story Narration",
    body: "Drug X launch is performing **strongly**, exceeding sales targets across all regions with **consistent growth** over the **last 6 months**. Market share is steadily increasing, with peak performance driven by key regional gains and improved patient adherence. Drug X has **consistently exceeded** sales targets over the past 6 months, with particularly **strong performance** in June–July 2024 (>109% achievement). Recent months show stabilised growth at **~105–106% of target**.",
    accent: true,
  },
  "memo-chart-1": {
    item_id: "memo-chart-1",
    type: "chart",
    title: "Revenue performance vs target",
    spec: {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      width: "container",
      layer: [
        {
          mark: { type: "bar", tooltip: true, color: "#9b59b6", opacity: 0.85 },
          encoding: {
            x: { field: "month", type: "ordinal", title: "Month" },
            y: { field: "net_sales", type: "quantitative", title: "Net Sales ($M)", axis: { title: "Net Sales ($M)" } },
          },
        },
        {
          mark: { type: "line", color: "#2196f3", point: true, strokeWidth: 2 },
          encoding: {
            x: { field: "month", type: "ordinal" },
            y: { field: "target", type: "quantitative" },
          },
        },
        {
          mark: { type: "line", color: "#ff9800", point: true, strokeWidth: 2 },
          encoding: {
            x: { field: "month", type: "ordinal" },
            y: { field: "achievement_pct", type: "quantitative" },
          },
        },
      ],
      resolve: { scale: { y: "independent" } },
      data: { values: [] },
    },
    narrative:
      "Net Sales have grown consistently from Jun 24 through Nov 24, tracking above the target line each month. Achievement percentage has stabilised in the 105–109% range.",
    ai_summary: {
      title: ">109% achievement",
      body: "Drug X has consistently exceeded sales targets over the past 6 months, with particularly strong performance in June–July 2024 (>109% achievement). Recent months show stabilised growth at ~105–106% of target.",
    },
    data_id: "memo-dat-chart-1",
  },
  "memo-chart-2": {
    item_id: "memo-chart-2",
    type: "chart",
    title: "Performance uptake by region",
    spec: {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      width: "container",
      mark: { type: "bar", tooltip: true },
      encoding: {
        x: { field: "uptake_pct", type: "quantitative", title: "Uptake (%)" },
        y: { field: "region", type: "nominal", sort: "-x", title: "Region" },
        color: {
          field: "uptake_pct",
          type: "quantitative",
          scale: { scheme: "purples" },
          legend: null,
        },
      },
      data: { values: [] },
    },
    narrative:
      "Texas leads regional uptake at 23.4%, followed by California and Florida. All top-10 states have exceeded their individual targets.",
    ai_summary: {
      title: "Texas leads national uptake",
      body: "Texas accounts for the highest single-state uptake in the country. The South-Central region as a whole is outperforming every other geography, suggesting the go-to-market approach deployed there should be replicated in the Midwest.",
    },
    data_id: "memo-dat-chart-2",
  },
  "memo-chart-3": {
    item_id: "memo-chart-3",
    type: "chart",
    title: "Market share trend",
    spec: {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      width: "container",
      mark: { type: "area", tooltip: true, opacity: 0.6 },
      encoding: {
        x: { field: "week", type: "ordinal", title: "Week" },
        y: { field: "share", type: "quantitative", title: "Market Share (%)" },
        color: { field: "metric", type: "nominal", title: "Metric" },
      },
      data: { values: [] },
    },
    narrative:
      "NBRx Share % has climbed from 1.4% in Week 1 to 3.2% in Week 12. TRx Share % follows a similar trajectory, reaching 2.6% by Week 12.",
    ai_summary: {
      title: "Accelerating market share gains",
      body: "Both NBRx and TRx share metrics are on a clear upward trend. The widening gap between NBRx and TRx share indicates new patients are converting faster than refills — a positive indicator for long-term volume growth.",
    },
    data_id: "memo-dat-chart-3",
  },
  "memo-insight-1": {
    item_id: "memo-insight-1",
    type: "insight",
    bullets: [
      "Sales target achievement of 107.7% reflects consistently strong execution across all key accounts over six months.",
      "Texas is the fastest-growing region with +18.5% MoM uplift — driven by improved HCP engagement and new formulary wins.",
      "NBRx market share grew 1.4 percentage points over 12 weeks, outpacing the closest competitor by 0.6 points.",
      "Patient adherence rates have improved to 82%, reducing early discontinuation risk and supporting TRx volume growth.",
      "June–July 2024 peak performance (>109% of target) aligns with the rollout of updated patient-support materials.",
    ],
  },
};

// ─── Dataset fixtures ──────────────────────────────────────────────────────────

export const MEMO_DATASETS: Record<string, CanvasDatasetApi> = {
  "memo-dat-chart-1": {
    dataset_id: "memo-dat-chart-1",
    rows: [
      { month: "Jun 24", net_sales: 7.1, target: 6.5, achievement_pct: 109.2 },
      { month: "Jul 24", net_sales: 8.4, target: 7.8, achievement_pct: 107.7 },
      { month: "Aug 24", net_sales: 12.2, target: 11.5, achievement_pct: 106.1 },
      { month: "Sep 24", net_sales: 15.6, target: 14.9, achievement_pct: 104.7 },
      { month: "Oct 24", net_sales: 18.3, target: 17.5, achievement_pct: 104.6 },
      { month: "Nov 24", net_sales: 19.1, target: 18.0, achievement_pct: 106.1 },
    ],
    page: 1,
    page_size: 100,
    total: 6,
  },
  "memo-dat-chart-2": {
    dataset_id: "memo-dat-chart-2",
    rows: [
      { region: "Texas",        uptake_pct: 23.4 },
      { region: "California",   uptake_pct: 19.8 },
      { region: "Florida",      uptake_pct: 17.2 },
      { region: "New York",     uptake_pct: 14.9 },
      { region: "Illinois",     uptake_pct: 12.3 },
      { region: "Ohio",         uptake_pct: 11.7 },
      { region: "Pennsylvania", uptake_pct: 10.5 },
      { region: "Georgia",      uptake_pct: 9.8 },
    ],
    page: 1,
    page_size: 100,
    total: 8,
  },
  "memo-dat-chart-3": {
    dataset_id: "memo-dat-chart-3",
    rows: [
      { week: "Week 1",  metric: "NBRx Share %", share: 1.4 },
      { week: "Week 1",  metric: "TRx Share %",  share: 1.2 },
      { week: "Week 4",  metric: "NBRx Share %", share: 1.9 },
      { week: "Week 4",  metric: "TRx Share %",  share: 1.6 },
      { week: "Week 8",  metric: "NBRx Share %", share: 2.5 },
      { week: "Week 8",  metric: "TRx Share %",  share: 2.1 },
      { week: "Week 12", metric: "NBRx Share %", share: 3.2 },
      { week: "Week 12", metric: "TRx Share %",  share: 2.6 },
    ],
    page: 1,
    page_size: 100,
    total: 8,
  },
};

// ─── Mock fetch functions ──────────────────────────────────────────────────────

export async function mockFetchMemoCanvasManifest(
  _workspaceId: string,
  _storyId: string,
): Promise<CanvasManifestApi> {
  await wait(MANIFEST_LATENCY_MS);
  return MEMO_MANIFEST;
}

export async function mockFetchMemoCanvasWidget(
  _workspaceId: string,
  _storyId: string,
  widgetId: string,
): Promise<CanvasWidgetApi> {
  const delay = WIDGET_LATENCY_MS[widgetId] ?? 500;
  await wait(delay);
  const widget = MEMO_WIDGETS[widgetId];
  if (!widget) throw new Error(`mockFetchMemoCanvasWidget: unknown widget "${widgetId}"`);
  return widget;
}

export async function mockFetchMemoCanvasDataset(
  _workspaceId: string,
  _storyId: string,
  dataId: string,
): Promise<CanvasDatasetApi> {
  await wait(DATASET_LATENCY_MS);
  const dataset = MEMO_DATASETS[dataId];
  if (!dataset) throw new Error(`mockFetchMemoCanvasDataset: unknown data_id "${dataId}"`);
  return dataset;
}

// ─── Exported for tests ────────────────────────────────────────────────────────

export const __MEMO_MANIFEST_FOR_TESTS__ = MEMO_MANIFEST;
export const __MEMO_WIDGETS_FOR_TESTS__ = MEMO_WIDGETS;
export const __MEMO_DATASETS_FOR_TESTS__ = MEMO_DATASETS;
